(ns ui.view.card.decisions.sub
  (:require
   [ui.util :as uu]
   [re-frame.core :as rf]
   [ui.view.card.decisions.model :as model]
   [ui.view.card.sub :as common-sub]
   [ui.state.session.sub :as session-sub]
   [ui.view.card.entity-list.util :as entity-list-util]
   [common.schema.entity-types :as entity-types]
   [common.util :as cu]
   [common.schema.registry :as registry]
   [common.localization :as loc]
   [frontend.state.permissions :as f-permissions]))

(rf/reg-sub
 ::loading?
 (fn [[_ ctx]]
   [(rf/subscribe [::common-sub/entities ctx])
    (rf/subscribe [::common-sub/loading-error? ctx])])
 (fn [[entities loading-error?]]
   (and (not loading-error?)
        (nil? entities))))

(rf/reg-sub
 ::create-decision-options
 (fn [[_ ctx]]
   [(rf/subscribe [::session-sub/decision-configuration])])
 (fn [[decision-configuration] [_ ctx]]
   (let [primary-entity-type (-> ctx :card :card/entity-type)
         enabled-decision-types
         (->> entity-types/decision-entity-types
              (filter (fn [entity-type]
                        (let [containing-entity-types
                              (get-in decision-configuration [:containing-entity-types entity-type])]
                          (some #{primary-entity-type} containing-entity-types)))))]
     (->> enabled-decision-types
          (sort-by loc/tr)
          (mapv (fn [decision-entity-type]
                  {:value decision-entity-type
                   :label (loc/tr decision-entity-type)}))))))

(rf/reg-sub
 ::expanded-decision-ids
 (fn [[_ ctx]]
   [(rf/subscribe [::common-sub/card-state ctx])])
 (fn [[card-state]]
   (get-in card-state model/expanded-decisions-path #{})))

(rf/reg-sub
 ::entity-associated-decisions
 (fn [[_ ctx]]
   [(rf/subscribe [::common-sub/entities ctx])])
 (fn [[card-entities-by-id] [_ {:keys [entity-id] :as _ctx}]]
   (->> card-entities-by-id
        vals
        (filterv (fn [entity]
                   (and (entity-types/decision-entity-types (:entity/type entity))
                        (-> entity
                            :common/containing-entity
                            (= entity-id))))))))

(rf/reg-sub
 ::entity-has-decisions?
 (fn [[_ ctx]]
   [(rf/subscribe [::entity-associated-decisions ctx])])
 (fn [[decisions]]
   (seq decisions)))

(rf/reg-sub
 ::decisions-table-data
 (fn [[_ ctx]]
   [(rf/subscribe [::entity-associated-decisions ctx])
    (rf/subscribe [::common-sub/entities ctx])
    (rf/subscribe [::session-sub/entities])
    (rf/subscribe [::session-sub/tenant])
    (rf/subscribe [::session-sub/tenant-schema])])
 (fn [[decisions card-entities-by-id global-entities-by-id tenant tenant-schema]
      [_ {:keys [card] :as _ctx}]]
   (let [all-entities-by-id (merge global-entities-by-id card-entities-by-id)
         column-fields (->> card
                            :card/config
                            :table
                            :columns
                            (mapv :field))]
     (entity-list-util/entities->table-row-data
      {:entities decisions
       :column-fields column-fields
       :entities-by-id all-entities-by-id
       :tenant tenant
       :tenant-schema tenant-schema}))))

(rf/reg-sub
 ::decision
 (fn [[_ ctx decision-id]]
   [(rf/subscribe [::common-sub/entities ctx])])
 (fn [[card-entities-by-id]
      [_ _ctx decision-id]]
   (get card-entities-by-id decision-id)))

(rf/reg-sub
 ::decision-date-display-string
 (fn [[_ ctx decision-id]]
   [(rf/subscribe [::decision ctx decision-id])
    (rf/subscribe [::session-sub/session-context])])
 (fn [[decision session-context]]
   (when (f-permissions/field-read-access? :decision/date
                                           (:entity/type decision)
                                           nil)
     (let [date (:decision/date decision)]
       (uu/field-value->materialized-value
        (merge session-context
               {:field :decision/date
                :value (:decision/date decision)
                :entity decision}))))))

(rf/reg-sub
 ::decision-notes
 (fn [[_ ctx decision-id]]
   [(rf/subscribe [::decision ctx decision-id])])
 (fn [[decision]]
   (let [entity-type (:entity/type decision)]
     (when (f-permissions/field-read-access? :decision/notes entity-type nil)
       (:decision/notes decision)))))


(defn field->notes-field
  [field]
  (let [field-str (cu/kw->str field)
        notes-field-str (str field-str "-notes")]
    (keyword notes-field-str)))

(rf/reg-sub
 ::decision-has-participants?
 (fn [[_ ctx decision-id]]
   [(rf/subscribe [::decision ctx decision-id])])
 (fn [[decision]]
   (->> decision
        :decision/participants
        seq)))

(rf/reg-sub
 ::decision-has-conditions?
 (fn [[_ ctx decision-id]]
   [(rf/subscribe [::decision ctx decision-id])])
 (fn [[decision]]
   (->> decision
        :decision/conditions
        seq)))

(rf/reg-sub
 ::decision-has-investment-reservations?
 (fn [[_ ctx decision-id]]
   [(rf/subscribe [::decision ctx decision-id])])
 (fn [[decision]]
   (->> decision
        :decision/investment-reservations
        seq)))

(rf/reg-sub
 ::decision-has-approvals-needed?
 (fn [[_ ctx decision-id]]
   [(rf/subscribe [::decision ctx decision-id])])
 (fn [[decision]]
   (some->> (case (:entity/type decision)
              :entity.type/aifm-decision
              model/aifm-decision-approvals-needed-default-fields
              :entity.type/aifm-exit-decision
              model/aifm-exit-decision-approvals-needed-default-fields
              nil)
            (filterv (fn [field]
                       (f-permissions/field-read-access? field
                                                         (:entity/type decision)
                                                         nil)))
            (mapv (fn [field]
                    (get decision field)))
            (remove nil?)
            seq)))

(defn resolve-field-list-data
  [{:keys [decision default-fields entities-by-id tenant tenant-schema]}]
  (let [entity-type (:entity/type decision)
        fields (->> default-fields
                    (filterv (fn [field]
                               (f-permissions/field-read-access? field
                                                                 entity-type
                                                                 nil))))]
    (->> fields
         (mapv (fn [field]
                 (let [field-schema (registry/get-field-schema tenant-schema field)
                       label (loc/field-name field field-schema)
                       value (get decision field)
                       notes-field (field->notes-field field)
                       read-access-to-notes-field? (f-permissions/field-read-access?
                                                    notes-field entity-type nil)
                       notes (when read-access-to-notes-field?
                               (get decision notes-field))
                       data-type (:field/data-type field-schema)
                       currency? (= data-type :data.type/currency-value)
                       materialized-value (uu/field-value->materialized-value
                                           {:field field
                                            :value value
                                            :tenant tenant
                                            :tenant-schema tenant-schema
                                            :id->entity entities-by-id
                                            :entity decision
                                            :show-currency? true})
                       ;; TODO: What other fields need to be converted?
                       value (if (and value currency?)
                               (uu/bigdec->float value)
                               value)]
                   {:field field
                    :field-label label
                    :value value
                    :materialized-value materialized-value
                    :notes notes
                    :notes? (cu/non-empty-string? notes)}))))))

(rf/reg-sub
 ::decision-details-data
 (fn [[_ ctx decision-id]]
   [(rf/subscribe [::decision ctx decision-id])
    (rf/subscribe [::common-sub/entities ctx])
    (rf/subscribe [::session-sub/entities])
    (rf/subscribe [::session-sub/session-context])])
 (fn [[decision card-entities-by-id global-entities-by-id session-context]
      [_ ctx decision-id]]
   (let [all-entities-by-id (merge global-entities-by-id
                                   card-entities-by-id)
         entity-type (:entity/type decision)

         default-fields (case entity-type
                          :entity.type/advisory-board-decision
                          model/advisory-board-decision-details-default-fields

                          :entity.type/ic-decision
                          model/ic-decision-details-default-fields

                          :entity.type/aifm-decision
                          model/aifm-decision-details-default-fields

                          :entity.type/aifm-exit-decision
                          model/aifm-exit-decision-details-default-fields

                          :entity.type/ic-exit-decision
                          model/ic-exit-decision-details-default-fields

                          (do
                            (js/console.log "UNMATCHED DECISION TYPE " entity-type)
                            []))]
     (resolve-field-list-data
      (merge
       (select-keys session-context [:tenant :tenant-schema])
       {:decision decision
        :default-fields default-fields
        :entities-by-id all-entities-by-id})))))

(rf/reg-sub
 ::decision-participants
 (fn [[_ ctx decision-id]]
   [(rf/subscribe [::decision ctx decision-id])
    (rf/subscribe [::common-sub/entities ctx])
    (rf/subscribe [::session-sub/entities])
    (rf/subscribe [::session-sub/session-context])])
 (fn [[decision card-entities-by-id global-entities-by-id]
      [_ ctx decision-id]]
   (let [participants (->> decision
                           :decision/participants
                           (mapv (fn [id]
                                   (or (get global-entities-by-id id)
                                       (get card-entities-by-id id)))))]
     participants)))

(rf/reg-sub
 ::decision-participants-table-config
 (fn []
   (let [columns (->> model/decision-participants-table-default-columns
                      (filterv (fn [{:keys [field]}]
                                 (f-permissions/field-read-access?
                                  field :entity.type/decision-condition nil))))]
     {:columns columns})))

(rf/reg-sub
 ::decision-participants-table-data
 (fn [[_ ctx decision-id]]
   [(rf/subscribe [::decision-participants ctx decision-id])
    (rf/subscribe [::decision-participants-table-config])
    (rf/subscribe [::common-sub/entities ctx])
    (rf/subscribe [::session-sub/entities])
    (rf/subscribe [::session-sub/legacy-entities])
    (rf/subscribe [::session-sub/session-context])])
 (fn [[decision-participants
       table-config
       card-entities-by-id
       global-entities-by-id
       legacy-entities-by-id
       session-context]
      [_ ctx decision-id]]
   (let [all-entities-by-id (merge global-entities-by-id
                                   legacy-entities-by-id
                                   card-entities-by-id)]
     (entity-list-util/entities->table-row-data
      (merge
       session-context
       {:entities decision-participants
        :column-fields (->> table-config
                            :columns
                            (mapv :field))
        :entities-by-id all-entities-by-id})))))

(rf/reg-sub
 ::decision-conditions-table-config
 (fn []
   (let [columns (->> model/decision-conditions-table-default-columns
                      (filterv (fn [{:keys [field]}]
                                 (f-permissions/field-read-access?
                                  field :entity.type/decision-condition nil))))]
     {:columns columns})))

(rf/reg-sub
 ::decision-conditions-table-data
 (fn [[_ ctx decision-id]]
   [(rf/subscribe [::decision ctx decision-id])
    (rf/subscribe [::decision-conditions-table-config])
    (rf/subscribe [::common-sub/entities ctx])
    (rf/subscribe [::session-sub/entities])
    (rf/subscribe [::session-sub/legacy-entities])
    (rf/subscribe [::session-sub/session-context])])
 (fn [[decision table-config
       card-entities-by-id
       global-entities-by-id
       legacy-entities
       session-context]
      [_ ctx decision-id]]
   (let [all-entities-by-id (merge global-entities-by-id
                                   legacy-entities
                                   card-entities-by-id)
         conditions (->> decision
                         :decision/conditions
                         (mapv (fn [id]
                                 (get all-entities-by-id id))))]
     (entity-list-util/entities->table-row-data
      (merge
       session-context
       {:entities conditions
        :column-fields (->> table-config
                            :columns
                            (mapv :field))
        :entities-by-id all-entities-by-id})))))

(rf/reg-sub
 ::decision-investment-reservations-table-config
 (fn [_]
   (let [columns (->> model/investment-reservation-table-default-columns
                      (filterv (fn [{:keys [field]}]
                                 (f-permissions/field-read-access? field
                                                                   :entity.type/investment-reservation
                                                                   nil))))]
     {:columns columns})))

(rf/reg-sub
 ::decision-investment-reservations-table-data
 (fn [[_ ctx decision-id]]
   [(rf/subscribe [::decision ctx decision-id])
    (rf/subscribe [::decision-investment-reservations-table-config ctx])
    (rf/subscribe [::common-sub/entities ctx])
    (rf/subscribe [::session-sub/entities])
    (rf/subscribe [::session-sub/session-context])])
 (fn [[decision table-config card-entities-by-id global-entities-by-id session-context]]
   (let [all-entities-by-id (merge global-entities-by-id
                                   card-entities-by-id)
         investment-reservations (->> decision
                                      :decision/investment-reservations
                                      (mapv (fn [id]
                                              (get global-entities-by-id id))))]
     (entity-list-util/entities->table-row-data
      (merge
       session-context
       {:entities investment-reservations
        :column-fields (->> table-config
                            :columns
                            (mapv :field))
        :entities-by-id all-entities-by-id})))))

(rf/reg-sub
 ::decision-approvals-needed-data
 (fn [[_ ctx decision-id]]
   [(rf/subscribe [::decision ctx decision-id])
    (rf/subscribe [::common-sub/entities ctx])
    (rf/subscribe [::session-sub/entities])
    (rf/subscribe [::session-sub/session-context])])
 (fn [[decision card-entities-by-id global-entities-by-id session-context]]
   (let [all-entities-by-id (merge global-entities-by-id
                                   card-entities-by-id)
         entity-type (:entity/type decision)

         default-fields (case entity-type
                          :entity.type/aifm-decision model/aifm-decision-approvals-needed-default-fields
                          nil)]
     (resolve-field-list-data
      (merge
       (select-keys session-context [:tenant :tenant-schema])
       {:decision decision
        :default-fields default-fields
        :entities-by-id all-entities-by-id})))))
